import { useCallback, useRef, useState } from "react"

import useResizeObserver, {
  UseResizeObserverCallback,
} from "@react-hook/resize-observer"

import { Tooltip, TooltipProps } from "./Tooltip"

type TruncateProps = {
  text: string
  middleEllipsis?: boolean
  ellipsisCharsCount?: number
  side?: TooltipProps["side"]
  disableTooltip?: boolean
}

export const Truncate = ({
  text,
  middleEllipsis,
  ellipsisCharsCount = 8,
  side = "top",
  disableTooltip,
}: TruncateProps) => {
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false)

  const children = (
    <TruncateInside
      text={text}
      ellipsisCharsCount={ellipsisCharsCount}
      middleEllipsis={middleEllipsis}
      onTrucated={setShouldShowTooltip}
    />
  )

  if (shouldShowTooltip && !disableTooltip) {
    return (
      <Tooltip
        content={text}
        side={side}
        className="max-w-md min-w-0"
        disableHoverablePopup
      >
        <div className="min-w-0">{children}</div>
      </Tooltip>
    )
  } else {
    return children
  }
}

const TruncateInside = ({
  text,
  ellipsisCharsCount = 8,
  middleEllipsis,
  onTrucated,
}: TruncateProps & { onTrucated: (value: boolean) => void }) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const textDividerindex = text.length - ellipsisCharsCount
  const preEllipsisText = text.substring(0, textDividerindex)
  const postEllipsisText = text.substring(textDividerindex, text.length)

  const resizeCallback = useCallback<UseResizeObserverCallback>(
    (entry) => {
      const shouldShow = entry.target.clientWidth < entry.target.scrollWidth
      onTrucated(shouldShow)
    },
    [onTrucated],
  )
  useResizeObserver(elementRef, resizeCallback)

  return (
    <div className="flex min-w-0">
      <div
        ref={elementRef}
        className="truncate whitespace-pre"
        key="truncate-container"
      >
        {middleEllipsis ? preEllipsisText : text}
      </div>
      {middleEllipsis && <div className="whitespace-pre">{postEllipsisText}</div>}
    </div>
  )
}
